// Purpose: creating new elements

// Imports from (only):
// Constants
// types

import {incrementGuid} from '@packages/utilities/guid';
import {Pair, pair, getFirst, getSecond} from '@packages/utilities/Pair';
import {
  UniqueIdsState,
  instantiateExpressionWithUniqueIds,
} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  ERecord,
  EList,
  EText,
  EBoolean,
  ExpressionElement,
  Expression,
  EEnum,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import {Plugins, Component} from '@packages/systems/wfdl';
import {defaultTextLink} from '@packages/systems/core/plugins';
import {ZWJ_RAW} from '@packages/systems/core/utils/TextUtils';
import {
  DEFAULT_LANGUAGE as DEFAULT_CODE_BLOCK_LANGUAGE,
  DEFAULT_THEME as DEFAULT_CODE_BLOCK_THEME,
} from '@packages/systems/core/constants/CodeBlock';

import {
  ADD_AI_PARAGRAPH,
  ADD_BLOCK_QUOTE,
  ADD_CODE_BLOCK,
  ADD_H1,
  ADD_H2,
  ADD_H3,
  ADD_H4,
  ADD_H5,
  ADD_H6,
  ADD_HTML_EMBED,
  ADD_IMAGE,
  ADD_MEDIA,
  ADD_OL,
  ADD_PARAGRAPH,
  ADD_UL,
  FORMAT_BUTTON,
  ADD_COMPONENT,
} from './constants';
import {CONTENT_EDITOR_ADD_TYPES} from './types';
import {needsContentEditorPlaintextMode} from './ContentEditorStateType';
import {ContentEditorActive} from '@packages/shared/states';
import type {ComponentMetadata} from '@packages/systems/component-utils';

const emptyListItem = {
  id: 'List Item',
  type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
  data: ERecord({
    list: ERecord({
      type: EText('item'),
    }),
    children: EList([]),
    text: EBoolean(false),
  }),
};

const paragraph = ({children}: {children: Expression}): ExpressionElement => {
  return {
    id: 'Paragraph',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({children}),
  };
};

export const createParagraphWithText = (
  textInParagraph: string,
  stringId: string
) => {
  const pId = incrementGuid(stringId);
  return pair(
    {
      id: pId,
      type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
      data: ERecord({
        children: EList([
          EElement({
            id: stringId,
            type: qualify(Plugins.name('Basic'), Component.name('String')),
            data: EText(textInParagraph),
          }),
        ]),
      }),
    },
    incrementGuid(pId)
  );
};

export const createListItem = ({id}: {id: string}) =>
  instantiateElement(emptyListItem, id);

export const createParagraph = ({
  id,
  children,
}: {
  id: string;
  children: Expression;
}) => instantiateElement(paragraph({children}), id);

const emptyZwjString = {
  id: 'String',
  type: qualify(Plugins.name('Basic'), Component.name('String')),
  data: EText(ZWJ_RAW),
};

const emptyString = {
  id: 'String',
  type: qualify(Plugins.name('Basic'), Component.name('String')),
  data: EText(''),
};

const whitespaceString = {
  id: 'String',
  type: qualify(Plugins.name('Basic'), Component.name('String')),
  data: EText(' '),
};

export const createParagraphWithZwj = (id: string) =>
  createParagraph({
    id,
    children: EList([EElement(emptyZwjString)]),
  });

export const instantiateElement = (
  element: ExpressionElement,
  id: string
): Pair<UniqueIdsState, ExpressionElement> => {
  const result = instantiateExpressionWithUniqueIds(element.data)(
    incrementGuid(id)
  );
  return pair(getFirst(result), {
    id,
    type: element.type,
    data: getSecond(result),
    ...(element.meta && {meta: element.meta}),
  });
};

export const createWhitespaceString = (id: string) =>
  instantiateElement(whitespaceString, id);

export const createEmptyString =
  ({id}: {id: string}) =>
  (state: ContentEditorActive) =>
    // in non-plaintext mode we use ZWJ to prevent the empty element being deleted by the browser. In plain text mode we only ever create strings so we don't need the ZWJ.
    instantiateElement(
      needsContentEditorPlaintextMode(state) ? emptyString : emptyZwjString,
      id
    );

const lineBreakElement = {
  id: 'Line Break',
  type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
  data: ERecord({}),
};

export const createLineBreak = (id: string) =>
  instantiateElement(lineBreakElement, id);

export const headingFactory =
  (tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => (guid: string) =>
    instantiateElement(
      {
        id: 'Heading',
        type: qualify(Plugins.name('Basic'), Component.name('Heading')),
        data: ERecord({
          tag: EEnum(tag),
          children: EList([EElement(emptyZwjString)]),
        }),
      },
      guid
    );

export const listFactory = (tag: 'ul' | 'ol') => (guid: string) =>
  instantiateElement(
    {
      id: 'List',
      type: qualify(Plugins.name('Basic'), Component.name('List')),
      data: ERecord({
        tag: EEnum(tag),
        list: ERecord({
          type: EText('list'),
        }),
        children: EList([
          EElement({
            id: 'List item',
            type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
            data: ERecord({
              list: ERecord({
                type: EText('item'),
              }),
              children: EList([EElement(emptyZwjString)]),
            }),
          }),
        ]),
      }),
    },
    guid
  );

type ContentEditorComponentParams = {
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  componentType?: ComponentMetadata['componentType'];
};
type ContentEditorCreation = {
  [key in CONTENT_EDITOR_ADD_TYPES]: (
    guid: string,
    params: ContentEditorComponentParams
  ) => Pair<UniqueIdsState, ExpressionElement>;
};

const createParagraphContentEditorCreation = (guid: string) =>
  instantiateElement(
    {
      id: 'Paragraph',
      type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
      data: ERecord({
        children: EList([EElement(emptyZwjString)]),
      }),
    },
    guid
  );

export const createStringElement = (text: string, guid: string) =>
  pair(
    {
      id: guid,
      type: qualify(Plugins.name('Basic'), Component.name('String')),
      data: EText(text),
    },
    incrementGuid(guid)
  );

export const blockquoteFactory = (id: string) =>
  instantiateElement(
    {
      id: 'Block Quote',
      type: qualify(Plugins.name('Basic'), Component.name('Blockquote')),
      data: ERecord({
        children: EList([EElement(emptyZwjString)]),
      }),
    },
    id
  );

export const createComponentElement = (
  guid: string,
  {componentType, plugins, components}: ContentEditorComponentParams
) => {
  // Null check with fallback to add a paragraph. This shouldn't happen if proper path was used.
  if (!componentType) {
    return contentEditorCreation[ADD_PARAGRAPH](guid, {plugins, components});
  }

  // Validate that the component exists in the component map with fallback to add a paragraph.
  if (!components.has(componentType)) {
    return contentEditorCreation[ADD_PARAGRAPH](guid, {plugins, components});
  }

  const instanceEl: ExpressionElement = {
    id: guid,
    type: componentType,
    data: ERecord({}),
  };
  const unique: UniqueIdsState = {
    id: incrementGuid(guid),
    idMap: {},
  };
  return pair(unique, instanceEl);
};

export const contentEditorCreation: ContentEditorCreation = {
  [ADD_BLOCK_QUOTE]: blockquoteFactory,
  [ADD_UL]: listFactory('ul'),
  [ADD_OL]: listFactory('ol'),
  [ADD_MEDIA]: (guid) =>
    instantiateElement(
      {
        id: 'Figure',
        type: qualify(Plugins.name('Basic'), Component.name('Figure')),
        data: ERecord({
          figure: ERecord({
            align: EText('center'),
            dimensions: EText(''),
            maxHeight: EText(''),
            maxWidth: EText(''),
            pageUrl: EText(''),
            type: EText('video'),
          }),
          style: ERecord({
            paddingBottom: EText(''),
          }),
          children: EList([
            EElement({
              id: 'Block',
              type: qualify(Plugins.name('Basic'), Component.name('Block')),
              data: ERecord({
                tag: EEnum('div'),
                children: EList([
                  EElement({
                    id: 'Iframe',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Iframe')
                    ),
                    data: ERecord({
                      iframe: ERecord({
                        type: EText('video'),
                      }),
                      attr: ERecord({
                        allowfullscreen: EText('true'),
                        frameborder: EText('0'),
                        scrolling: EText('no'),
                        src: EText(''),
                        title: EText(''),
                      }),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      },
      guid
    ),
  [ADD_HTML_EMBED]: (guid: string) =>
    instantiateElement(
      {
        id: 'HtmlEmbed',
        type: qualify(Plugins.name('Embed'), Component.name('HtmlEmbed')),
        data: ERecord({
          embed: ERecord({
            type: EText('html'),
          }),
          insideRTE: EBoolean(true),
          children: EList([]),
        }),
      },
      guid
    ),
  [ADD_IMAGE]: (guid: string) =>
    instantiateElement(
      {
        id: 'Figure',
        type: qualify(Plugins.name('Basic'), Component.name('Figure')),
        data: ERecord({
          figure: ERecord({
            align: EText('center'),
            type: EText('image'),
          }),
          children: EList([
            EElement({
              id: 'Block',
              type: qualify(Plugins.name('Basic'), Component.name('Block')),
              data: ERecord({
                tag: EEnum('div'),
                children: EList([
                  EElement({
                    id: 'Image',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Image')
                    ),
                    data: ERecord({
                      attr: ERecord({
                        src: EText(
                          'https://d3e54v103j8qbb.cloudfront.net/img/image-placeholder.svg'
                        ),
                        loading: EEnum('lazy'),
                        alt: EText('__wf_reserved_inherit'),
                      }),
                      img: ELiteral({
                        name: qualify(Plugins.name('Asset'), typeName('Image')),
                        value: {},
                      }),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      },
      guid
    ),
  [ADD_CODE_BLOCK]: (guid: string) =>
    instantiateElement(
      {
        id: 'Code Block',
        type: qualify(Plugins.name('Basic'), Component.name('CodeBlock')),
        data: ERecord({
          code: EText(''),
          language: EEnum(DEFAULT_CODE_BLOCK_LANGUAGE),
          theme: EEnum(DEFAULT_CODE_BLOCK_THEME),
          lineNumbers: EBoolean(false),
        }),
      },
      guid
    ),
  [ADD_H1]: headingFactory('h1'),
  [ADD_H2]: headingFactory('h2'),
  [ADD_H3]: headingFactory('h3'),
  [ADD_H4]: headingFactory('h4'),
  [ADD_H5]: headingFactory('h5'),
  [ADD_H6]: headingFactory('h6'),
  [ADD_PARAGRAPH]: createParagraphContentEditorCreation,
  [ADD_AI_PARAGRAPH]: createParagraphContentEditorCreation,
  [ADD_COMPONENT]: createComponentElement,
};

type FormatElementCreation = {
  [key in FORMAT_BUTTON]: (
    guid: string
  ) => Pair<UniqueIdsState, ExpressionElement>;
};

export const formatElementCreation: FormatElementCreation = {
  bold: (seedId: string) =>
    instantiateElement(
      {
        id: 'Strong',
        type: qualify(Plugins.name('Basic'), Component.name('Strong')),
        data: ERecord({}),
      },
      seedId
    ),
  italic: (seedId) =>
    instantiateElement(
      {
        id: 'Strong',
        type: qualify(Plugins.name('Basic'), Component.name('Emphasized')),
        data: ERecord({}),
      },
      seedId
    ),
  link: (seedId) => instantiateElement(defaultTextLink, seedId),
  span: (seedId) =>
    instantiateElement(
      {
        id: 'Span',
        type: qualify(Plugins.name('Basic'), Component.name('Span')),
        data: ERecord({}),
      },
      seedId
    ),
  superscript: (seedId) =>
    instantiateElement(
      {
        id: 'Superscript',
        type: qualify(Plugins.name('Basic'), Component.name('Superscript')),
        data: ERecord({}),
      },
      seedId
    ),
  subscript: (seedId) =>
    instantiateElement(
      {
        id: 'Subscript',
        type: qualify(Plugins.name('Basic'), Component.name('Subscript')),
        data: ERecord({}),
      },
      seedId
    ),
  inlineCode: (seedId) =>
    instantiateElement(
      {
        id: 'InlineCode',
        type: qualify(Plugins.name('Basic'), Component.name('InlineCode')),
        data: ERecord({}),
      },
      seedId
    ),
};
